// dependencies
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Spinner } from "@performyard/py-components";

import Text from "../../../../../../../components/Text";
// components
import { CardBody, CardHeader } from "../../../../../components/Card";
import SatisfactionMeter from "../../components/SatisfactionMeter";
// css
import css from "../../components/SatisfactionMeter/SatisfactionMeter.module.scss";

// overview store
import useStore from "../../../../../store";

// hooks
import { useCohortSatisfactionRange } from "../../../../../../../api/hooks";

// config
import Config from "../../../../../Config";

// constants
const MIN_CHARS_TO_DISPLAY_TITLE = 20;

interface SatisfactionRangeProps {
	superlative: "most" | "least";
}

const SatisfactionRange = (props: SatisfactionRangeProps) => {
	const { superlative } = props;

	// setup
	const navigate = useNavigate();
	const [filter] = useStore((state) => [state.filter]);

	const {
		data: cohortSummary,
		isLoading: isScoreLoading,
		error: scoreError,
	} = useCohortSatisfactionRange({ startDate: filter.startDate, endDate: filter.endDate });

	const cohort = useMemo(() => {
		const cohort = superlative === "most" ? cohortSummary?.max : cohortSummary?.min;
		return cohort;
	}, [JSON.stringify(cohortSummary)]);

	// render card
	return (
		<>
			<CardHeader
				title={
					<span className={css.cohortName}>
						<Text element="span" size="paragraph-3" weight="regular">
							{superlative.charAt(0).toUpperCase() + superlative.slice(1)} Satisfied
						</Text>{" "}
						<span
							title={
								cohort?.name && cohort.name.length > MIN_CHARS_TO_DISPLAY_TITLE
									? cohort.name
									: undefined
							}
						>
							<Text element="span" size="paragraph-3" weight="bold" testID="cohort-name">
								{cohort?.name}
							</Text>
						</span>
					</span>
				}
				info={
					superlative === "most"
						? "The cohort with the highest overall satisfaction for the selected time period."
						: "The cohort with the lowest overall satisfaction for the selected time period."
				}
				options={[
					{
						id: "view-cohorts",
						label: "Cohorts",
						action: () => {
							// navigate to /engagement/cohorts page
							navigate(Config.ROUTES.COHORTS.url);
						},
					},
				]}
				style={{ paddingBottom: "16px" }}
			/>
			<CardBody>
				<div className={css.cardBody}>
					<div className={css.summary}>
						{scoreError ? (
							<Text size="paragraph-3" weight="regular">
								Error fetching score
							</Text>
						) : (
							<>
								{isScoreLoading || !cohortSummary ? (
									<span className={`${css.spinRow} ${css.score}`}>
										<Spinner size={20} />
									</span>
								) : cohort ? (
									<SatisfactionMeter
										overallAverage={cohort.average}
										monthlyData={cohort.monthlyAverages}
									/>
								) : null}
							</>
						)}
					</div>
				</div>
			</CardBody>
		</>
	);
};

export default SatisfactionRange;
